import React from 'react';
import { Grid, Box, Typography, Divider } from '@mui/material';
import { Payment as PaymentIcon, Info as InfoIcon } from '@mui/icons-material';
import { PaymentConfig, OrderSummary } from '../../../../types/sales.types';
import { formatCurrency } from '../../../../utils/formatters';
import { usePaymentInformationCard } from './PaymentInformationCard.hooks';
import {
  StyledCard,
  StyledCardContent,
  CardTitle,
  InfoBox,
  InfoLabel,
  InfoValue,
} from './PaymentInformationCard.styles';

interface PaymentMethodOption {
  id: string;
  name: string;
  icon: any;
  color: string;
}

interface PaymentInformationCardProps {
  paymentConfig: PaymentConfig;
  orderSummary: OrderSummary;
  paymentMethodOptions: PaymentMethodOption[];
}

const PaymentInformationCard: React.FC<PaymentInformationCardProps> = ({
  paymentConfig,
  orderSummary,
  paymentMethodOptions,
}) => {
  const {
    formattedCashAmount,
    formattedChange,
    formattedPendingBalance,
    paymentMethodName,
    partialPaymentStatusText,
    partialPaymentColor,
    showCashInfo,
    showChange,
    showPendingBalance,
    isPartialPayment,
    hasRegisteredPayments,
    resolvePaymentMethodName,
  } = usePaymentInformationCard({ paymentConfig, orderSummary, paymentMethodOptions });

  return (
    <StyledCard>
      <StyledCardContent>
        <CardTitle variant="h6">
          <PaymentIcon color="primary" />
          Información de Pago
        </CardTitle>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <InfoBox>
              <InfoLabel variant="body2">Método de Pago</InfoLabel>
              <InfoValue variant="body1">{paymentMethodName}</InfoValue>
            </InfoBox>

            {showCashInfo && (
              <>
                <InfoBox>
                  <InfoLabel variant="body2">Monto Recibido</InfoLabel>
                  <InfoValue variant="body1">{formattedCashAmount}</InfoValue>
                </InfoBox>

                {showChange && (
                  <InfoBox>
                    <InfoLabel variant="body2">Cambio</InfoLabel>
                    <InfoValue variant="body1" sx={{ color: 'success.main' }}>
                      {formattedChange}
                    </InfoValue>
                  </InfoBox>
                )}
              </>
            )}
          </Grid>

          <Grid item xs={6}>
            <InfoBox>
              <InfoLabel variant="body2">Pago Parcial</InfoLabel>
              <InfoValue variant="body1" sx={{ color: partialPaymentColor }}>
                {partialPaymentStatusText}
              </InfoValue>
            </InfoBox>

            {showPendingBalance && (
              <InfoBox>
                <InfoLabel variant="body2">Saldo Pendiente</InfoLabel>
                <InfoValue variant="body1" sx={{ color: 'warning.main' }}>
                  {formattedPendingBalance}
                </InfoValue>
              </InfoBox>
            )}
          </Grid>
        </Grid>

        {hasRegisteredPayments && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
              Pagos registrados
            </Typography>
            {paymentConfig.payments.map((payment, index) => {
              const noteText = payment.paymentNotes?.trim() ?? '';
              const hasNotes = noteText !== '';
              const isCash = payment.method === 'CASH';
              const methodName = resolvePaymentMethodName(payment.method);

              return (
                <Box key={payment.id}>
                  {index > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {methodName} — {formatCurrency(payment.amount)}
                    </Typography>
                    {isCash &&
                      payment.cashReceived != null &&
                      payment.cashReceived > payment.amount && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          Recibido: {formatCurrency(payment.cashReceived)} · Cambio:{' '}
                          {formatCurrency(payment.cashReceived - payment.amount)}
                        </Typography>
                      )}
                    {hasNotes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                      >
                        {noteText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Payment Alerts */}
        {isPartialPayment && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: 'rgba(245, 158, 11, 0.06)',
              borderRadius: '8px',
              padding: '10px 14px',
              mt: 3,
            }}
          >
            <InfoIcon sx={{ fontSize: 18, color: '#f59e0b', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: '#f59e0b', mb: 0.5 }}>
                Pago Parcial
              </Typography>
              <Typography variant="body2" sx={{ color: '#f59e0b', lineHeight: 1.5 }}>
                Esta venta quedará registrada con un saldo pendiente de {formattedPendingBalance}.
                El cliente podrá completar el pago posteriormente.
              </Typography>
            </Box>
          </Box>
        )}

        {showChange && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: 'rgba(16, 185, 129, 0.06)',
              borderRadius: '8px',
              padding: '10px 14px',
              mt: 3,
            }}
          >
            <InfoIcon sx={{ fontSize: 18, color: '#10b981', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: '#10b981', mb: 0.5 }}>
                Cambio a Entregar
              </Typography>
              <Typography variant="body2" sx={{ color: '#10b981', lineHeight: 1.5 }}>
                Debe entregar {formattedChange} de cambio al cliente.
              </Typography>
            </Box>
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

export default PaymentInformationCard;
